import React from 'react';
import { useState, useMemo, useContext } from 'react';
import { Stack, Typography, ToggleButtonGroup, ToggleButton, Divider } from '@mui/material';
import { useGetImplementerInboxQuery } from '../../api/implementerInboxSlice';
import LoadingIndicator from '../LoadingIndicator';
import PhysicianEntry from '../StaffReviewPortal/v2/PhysicianEntry';
import { theme } from '../../theme';
import { UserContext } from '../Hooks/useUser';


function ImplementerInbox() {
    const [activeTab, setActiveTab] = useState(sessionStorage.getItem('activeImplementerInboxTab') || 'ready');
    const [selectedPhysician, setSelectedPhysician] = useState(null);
    const { user } = useContext(UserContext);

    const { data: activeReviews = [], isLoading: activeReviewsLoading } = useGetImplementerInboxQuery({
        filter: 'ready',
    });

    const { data: pendingReviews = [], isLoading: pendingReviewsLoading } = useGetImplementerInboxQuery({
        filter: 'pending',
    });

    const { data: completeReviews = [], isLoading: completeReviewsLoading } = useGetImplementerInboxQuery({
        filter: 'complete',
    });

    const shouldShowLoadingIndicator = useMemo(() => {
        return (activeTab === 'ready' && activeReviewsLoading) || 
            (activeTab === 'pending' && pendingReviewsLoading) || 
            (activeTab === 'complete' && completeReviewsLoading);
    }, [activeReviewsLoading, pendingReviewsLoading, completeReviewsLoading, activeTab]);

    function handleTabChange(value) {
        if (value !== null) {
            setActiveTab(value);
            sessionStorage.setItem('activeImplementerInboxTab', value);
        }
    }

    function getTabSupportingText() {
        switch (activeTab) {
        case 'ready':
            return 'These optimizations have been reviewed by physicians and card managers and are ready for you to implement.';
        case 'pending':
            return 'These optimizations are currently being reviewed by card managers. If approved, they will appear in the \'Ready for Implementation\' tab.';
        case 'complete':
            return 'These optimizations have already been implemented. No action required from you!';
        default:
            return '';
        }
    }

    const formattedDrafts = useMemo(() => {
        var dataToReturn = {};

        var reviews = [];

        if (activeTab === 'ready') {
            reviews = activeReviews;
        } else if (activeTab === 'pending') {
            reviews = pendingReviews;
        } else if (activeTab === 'complete') {
            reviews = completeReviews;
        }

        reviews.forEach((review) => {
            if (dataToReturn[review.card.hospital.name] === undefined) {
                dataToReturn[review.card.hospital.name] = {};
            }
            if (dataToReturn[review.card.hospital.name][review.card.doctor.name] === undefined) {
                dataToReturn[review.card.hospital.name][review.card.doctor.name] = [];
            }

            dataToReturn[review.card.hospital.name][review.card.doctor.name].push(review);
        });


        return dataToReturn;
    }, [activeReviews, pendingReviews, completeReviews, activeTab]);

    return (
        <Stack spacing={3} sx={{ height: '100%' }}>
            <Stack direction='row' px={4} pb={1} alignItems='center' justifyContent='space-between' sx={{ top: 64, position: 'sticky', backgroundColor: theme.palette.background.white, borderBottom: '1px solid', borderColor: 'divider' }}>
                <Stack spacing={1}>
                    <Typography variant='heavy' fontSize='18px'><b>Implementer Inbox</b></Typography>
                    <ToggleButtonGroup value={activeTab} exclusive onChange={(event, newValue) => handleTabChange(newValue)} color='primary'>
                        <ToggleButton value='ready'>
                            <Typography variant='normal' fontSize='14px'>Ready for Implementation</Typography>
                        </ToggleButton>
                        <ToggleButton value='pending'>
                            <Typography variant='normal' fontSize='14px'>Card Manager Review</Typography>
                        </ToggleButton>
                        <ToggleButton value='complete'>
                            <Typography variant='normal' fontSize='14px'>Implementation Complete</Typography>
                        </ToggleButton>
                    </ToggleButtonGroup>
                    <Typography variant='normal' fontSize='14px'>{getTabSupportingText()}</Typography>
                </Stack>
                <Stack textAlign='end'>
                    <Typography variant='heavy' sx={{ fontSize: '16px' }}>{user?.name}</Typography>
                    <Typography variant='normal' color={theme.palette.subtext.main} sx={{ fontSize: '14px' }}>{user?.hospital?.name}</Typography>
                </Stack>
            </Stack>
            <Stack px={4} spacing={2}>
                {shouldShowLoadingIndicator ? (
                    <LoadingIndicator variant='inbox' />
                ) : (
                    formattedDrafts && Object.keys(formattedDrafts).length > 0 ? (
                        Object.keys(formattedDrafts).sort().map((hospital) => (
                            <Stack key={hospital} spacing={2}>
                                <Typography variant='heavy' fontSize='16px'><b>{hospital}</b></Typography>
                                <Stack width='100%' divider={<Divider />} sx={{
                                    border: '1px solid',
                                    borderRadius: '8px',
                                    borderColor: theme.palette.border.main,
                                    backgroundColor: 'white',
                                }}>
                                    {Object.keys(formattedDrafts[hospital]).sort((a, b) => a.split(' ')[1].localeCompare(b.split(' ')[1])).map((doctor) => (
                                        <PhysicianEntry
                                            key={formattedDrafts[hospital][doctor][0].card.doctor.id + '|' + hospital}
                                            hospital={hospital}
                                            physician={formattedDrafts[hospital][doctor][0].card.doctor}
                                            drafts={formattedDrafts[hospital][doctor]}
                                            status={'active'}
                                            selectedPhysician={selectedPhysician}
                                            setSelectedPhysician={setSelectedPhysician}
                                            infoText={formattedDrafts[hospital][doctor].length + ' Card' + (formattedDrafts[hospital][doctor].length === 1 ? '' : 's')}
                                            variant='implementer'
                                        />
                                    ))}
                                </Stack>
                            </Stack>
                        ))
                    ) : (
                        <Typography variant='normal' fontSize='14px'><b>No reviews available at this time</b></Typography>
                    )
                )}
            </Stack>
        </Stack>
    );
}

export default ImplementerInbox;
